import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  Breadcrumbs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Home as HomeIcon,
  Event as EventIcon,
  Group as GroupIcon,
  ManageAccounts as ManageAccountsIcon,
  LocationOn as LocationOnIcon,
  AccessTime as AccessTimeIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { groupPlayAPI } from '../../services/api';
import { GroupPlay, GroupPlayInstance, GroupPlayStatus } from '../../types';
import { formatDate, formatTimeString } from '../../utils/dateUtils';

const ViewGroupPlayPage: React.FC = () => {
  const { groupPlayId } = useParams<{ groupPlayId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const [groupPlayData, setGroupPlayData] = useState<GroupPlay | null>(null);
  const [groupPlayInstances, setGroupPlayInstances] = useState<GroupPlayInstance[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [instancesLoading, setInstancesLoading] = useState<boolean>(false);
  const [sessionStatusFilter, setSessionStatusFilter] = useState<string>('scheduled');

  // Determine referrer for breadcrumbs - check location state or fallback to management page
  const referrer = (location.state as any)?.from || 'group-play-management';
  
  const getBreadcrumbPath = () => {
    switch (referrer) {
      case 'dashboard':
        return { label: 'Dashboard', icon: <HomeIcon />, path: '/dashboard' };
      case 'calendar':
        return { label: 'Calendar', icon: <EventIcon />, path: '/calendar' };
      case 'group-play-management':
      default:
        return { label: 'Group Play Management', icon: <GroupIcon />, path: '/group-plays' };
    }
  };
  
  const breadcrumbPath = getBreadcrumbPath();

  useEffect(() => {
    if (groupPlayId) {
      fetchGroupPlayData();
    }
  }, [groupPlayId]);

  const fetchGroupPlayData = async () => {
    if (!groupPlayId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await groupPlayAPI.getGroupPlayById(groupPlayId);
      setGroupPlayData(response.data);
      
      // Fetch group play instances if user has appropriate permissions
      if (user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'employee' || user?.role === 'coach') {
        fetchGroupPlayInstances();
      }
    } catch (error: any) {
      console.error('Error fetching group play:', error);
      setError(error.response?.data?.message || 'Failed to fetch group play details');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupPlayInstances = async () => {
    if (!groupPlayId) return;

    setInstancesLoading(true);
    try {
      const response = await groupPlayAPI.getGroupPlayInstances(groupPlayId);
      setGroupPlayInstances(response.data || []);
    } catch (error: any) {
      console.error('Error fetching group play instances:', error);
      // Don't set main error state for instances fetch failures
    } finally {
      setInstancesLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'scheduled':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'completed':
        return 'default';
      default:
        return 'default';
    }
  };

  const canEditGroupPlay = () => {
    return user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'employee' || user?.role === 'coach';
  };

  const handleEditGroupPlay = () => {
    navigate(`/group-plays/edit/${groupPlayId}`);
  };

  const handleViewInstance = (instance: GroupPlayInstance) => {
    navigate(`/group-plays/${groupPlayId}/instances/${instance.id}`, {
      state: { from: 'group-play-details' }
    });
  };

  const getDayName = (dayNumber: number) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayNumber];
  };

  const formatRecurringPattern = (pattern: any) => {
    if (!pattern) return 'N/A';
    
    if (pattern.frequency === 'weekly') {
      const days = pattern.daysOfWeek?.map((d: number) => getDayName(d)).join(', ') || 'N/A';
      return `Weekly on ${days}`;
    } else if (pattern.frequency === 'monthly') {
      return `Monthly on day ${pattern.dayOfMonth || 'N/A'}`;
    } else if (pattern.frequency === 'daily') {
      return `Daily`;
    }
    
    return pattern.frequency || 'N/A';
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  if (!groupPlayData) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="warning">Group play not found</Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Button
            component={RouterLink}
            to="/dashboard"
            color="inherit"
            startIcon={<HomeIcon />}
            sx={{ textTransform: 'none' }}
          >
            Dashboard
          </Button>
          {referrer !== 'dashboard' && (
            <Button
              component={RouterLink}
              to={breadcrumbPath.path}
              color="inherit"
              startIcon={breadcrumbPath.icon}
              sx={{ textTransform: 'none' }}
            >
              {breadcrumbPath.label}
            </Button>
          )}
          <Typography color="text.primary">Group Play Details</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
              <GroupIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
              {groupPlayData.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {groupPlayData.description}
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              variant="outlined"
            >
              Back
            </Button>
            {canEditGroupPlay() && (
              <Button
                startIcon={<EditIcon />}
                onClick={handleEditGroupPlay}
                variant="contained"
              >
                Edit Group Play
              </Button>
            )}
          </Stack>
        </Box>

        <Grid container spacing={3}>
          {/* Main Group Play Information */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Group Play Information
              </Typography>
              <Grid container spacing={3}>
                {groupPlayData.type && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      Type
                    </Typography>
                    <Typography variant="body1">
                      {groupPlayData.type}
                    </Typography>
                  </Grid>
                )}
                {(groupPlayData.coachName || groupPlayData.guestCoachName) && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      Coach
                    </Typography>
                    <Typography variant="body1">
                      {groupPlayData.isGuestCoach ? groupPlayData.guestCoachName : groupPlayData.coachName}
                      {groupPlayData.isGuestCoach && (
                        <Chip label="Guest" size="small" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    {!groupPlayData.isGuestCoach && groupPlayData.coachEmail && (
                      <Typography variant="body2" color="text.secondary">
                        {groupPlayData.coachEmail}
                      </Typography>
                    )}
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Capacity
                  </Typography>
                  <Typography variant="body1">
                    {groupPlayData.capacity} players
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Waitlist
                  </Typography>
                  <Typography variant="body1">
                    {groupPlayData.isWaitlistAvailable ? 'Available' : 'Not Available'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Member Session Fee
                  </Typography>
                  <Typography variant="body1">
                    ${groupPlayData.memberSessionFee || '0.00'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Guest Session Fee
                  </Typography>
                  <Typography variant="body1">
                    ${groupPlayData.guestSessionFee || '0.00'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Guest Bookings
                  </Typography>
                  <Typography variant="body1">
                    {groupPlayData.allowGuestBookings ? 'Allowed' : 'Not Allowed'}
                  </Typography>
                </Grid>
                {!groupPlayData.recurringPattern && (
                  <>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        Schedule Date
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(groupPlayData.scheduleDate)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        Start Time
                      </Typography>
                      <Typography variant="body1">
                        {formatTimeString(groupPlayData.startTime)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        End Time
                      </Typography>
                      <Typography variant="body1">
                        {formatTimeString(groupPlayData.endTime)}
                      </Typography>
                    </Grid>
                  </>
                )}
                {(groupPlayData.venueNames || groupPlayData.venues) && (groupPlayData.venueNames?.length || groupPlayData.venues.length) > 0 && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" color="text.secondary">
                      Venues
                    </Typography>
                    <Typography variant="body1">
                      {(groupPlayData.venueNames || groupPlayData.venues).join(', ')}
                    </Typography>
                  </Grid>
                )}
                {groupPlayData.notes && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body1">
                      {groupPlayData.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {groupPlayData.recurringPattern && (
                <>
                  <Box sx={{ mt: 3, mb: 2 }}>
                    <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
                      Recurring Schedule
                    </Typography>
                  </Box>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        Pattern
                      </Typography>
                      <Typography variant="body1">
                        {formatRecurringPattern(groupPlayData.recurringPattern)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        Start Date
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(groupPlayData.scheduleDate)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        End Date
                      </Typography>
                      <Typography variant="body1">
                        {groupPlayData.recurringPattern.endDate 
                          ? formatDate(groupPlayData.recurringPattern.endDate) 
                          : 'Ongoing'}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        Session Time
                      </Typography>
                      <Typography variant="body1">
                        {formatTimeString(groupPlayData.startTime)} - {formatTimeString(groupPlayData.endTime)}
                      </Typography>
                    </Grid>
                  </Grid>
                </>
              )}
            </Paper>

            {/* Group Play Instances Section (for admin/employee/coach users) */}
            {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'employee' || user?.role === 'coach') && (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Sessions ({sessionStatusFilter === 'all' ? groupPlayInstances.length : groupPlayInstances.filter(i => i.status?.toLowerCase() === sessionStatusFilter).length})
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="session-status-filter-label">Status</InputLabel>
                    <Select
                      labelId="session-status-filter-label"
                      value={sessionStatusFilter}
                      label="Status"
                      onChange={(e) => setSessionStatusFilter(e.target.value)}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="scheduled">Scheduled</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                {instancesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : groupPlayInstances.filter(i => sessionStatusFilter === 'all' || i.status?.toLowerCase() === sessionStatusFilter).length > 0 ? (
                  <Grid container spacing={2}>
                    {groupPlayInstances.filter(i => sessionStatusFilter === 'all' || i.status?.toLowerCase() === sessionStatusFilter).map((instance) => (
                      <Grid size={{ xs: 12, md: 6 }} key={instance.id}>
                        <Card variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                  {formatDate(instance.sessionDate)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {formatTimeString(instance.startTime)} - {formatTimeString(instance.endTime)}
                                </Typography>
                              </Box>
                              <Chip
                                label={instance.status}
                                color={getStatusColor(instance.status)}
                                size="small"
                              />
                            </Box>
                            
                            {(instance.venueNames && instance.venueNames.length > 0) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <LocationOnIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                  {instance.venueNames.join(', ')}
                                </Typography>
                              </Box>
                            )}
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <PeopleIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {instance.confirmedCount || 0} / {instance.capacity} confirmed
                                {(instance.waitlistCount ? instance.waitlistCount > 0 && 
                                  <> • {instance.waitlistCount} on waitlist</>
                                :<></>)}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleViewInstance(instance)}
                              >
                                View Details
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<ManageAccountsIcon />}
                                onClick={() => handleViewInstance(instance)}
                              >
                                Manage Bookings
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {groupPlayInstances.length === 0
                      ? (groupPlayData?.recurringPattern ? 'No instances generated yet.' : 'This group play has no scheduled instances.')
                      : `No ${sessionStatusFilter === 'all' ? '' : sessionStatusFilter + ' '}sessions found.`}
                  </Typography>
                )}
              </Paper>
            )}
          </Grid>

          {/* Status Sidebar */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Status
                </Typography>
                <Chip
                  label={groupPlayData.active ? 'Active' : 'Inactive'}
                  color={getStatusColor(groupPlayData.active ? 'active' : 'inactive')}
                  sx={{ mb: 2 }}
                />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Current Status
                </Typography>
                <Chip
                  label={groupPlayData.status}
                  color={getStatusColor(groupPlayData.status)}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default ViewGroupPlayPage;
